import React from "react";
import { FaTrash, FaLock, FaStar } from "react-icons/fa";
import useCollection from "../../Collection/hooks/useCollection";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";

interface CollectionListItemProps {
  collectionId: string;
  isPermanent?: boolean;
  isThemed?: boolean;
  canRemove?: boolean;
  onRemove?: (id: string) => void;
}

const rowSx = {
  display: "flex",
  alignItems: "center",
  p: "10px",
  borderBottom: "1px solid",
  borderColor: "divider",
};

function CollectionListItem({
  collectionId,
  isPermanent = false,
  isThemed = false,
  canRemove = false,
  onRemove,
}: CollectionListItemProps) {
  const { data: collection, isLoading } = useCollection(collectionId);

  if (isLoading) {
    return (
      <DSBox sx={rowSx}>
        <DSCircularProgress size={20} />
        <DSTypography sx={{ ml: "10px" }}>Loading...</DSTypography>
      </DSBox>
    );
  }

  if (!collection) {
    return (
      <DSBox sx={rowSx}>
        <DSTypography sx={{ color: "text.secondary" }}>
          Collection not found
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox sx={rowSx}>
      {collection.logoUrl && (
        <img
          src={collection.logoUrl}
          alt={collection.name}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            marginRight: 10,
            objectFit: "cover",
          }}
        />
      )}
      <DSBox sx={{ flex: 1 }}>
        <DSTypography sx={{ fontWeight: 500 }}>{collection.name}</DSTypography>
        {collection.description && (
          <DSTypography
            sx={{ fontSize: 12, color: "text.secondary", mt: "2px" }}
          >
            {collection.description}
          </DSTypography>
        )}
      </DSBox>
      {isPermanent && (
        <DSBox
          sx={{ color: "primary.main", ml: "10px", display: "flex" }}
          title="Permanent collection"
        >
          <FaLock />
        </DSBox>
      )}
      {isThemed && (
        <DSBox
          sx={{ color: "primary.main", ml: "10px", display: "flex" }}
          title="Themed collection"
        >
          <FaStar />
        </DSBox>
      )}
      {canRemove && onRemove && (
        <DSBox
          sx={{
            color: "error.main",
            cursor: "pointer",
            ml: "10px",
            display: "flex",
          }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onRemove(collectionId);
          }}
        >
          <FaTrash />
        </DSBox>
      )}
    </DSBox>
  );
}

export default CollectionListItem;
